import { useEffect, useState } from 'react';
import { type Locale } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import type { FallbackLanguage, Language } from '../types';
import { FALLBACK_LANGUAGE } from '../config/localization';
import { useRequestStatus } from './useRequestStatus';
import { useAppSettingsStore } from './useAppSettingsStore';

const dateLocaleMap: Record<
  Exclude<Language, FallbackLanguage>,
  () => Promise<Locale>
> = {
  // Skip en-US as it's default locale in date-fns source code
  // and it's already imported statically
  ru: () => import('date-fns/locale/ru').then(module => module.ru),
};

export function useDateLocale() {
  const { getLanguage } = useAppSettingsStore();
  const [dateLocale, setDateLocale] = useState<Locale>(enUS);
  const {
    isLoading: isDateLocaleLoading,
    setIsLoading: setIsDateLocaleLoading,
  } = useRequestStatus();

  const language = getLanguage();

  useEffect(() => {
    async function load() {
      try {
        setIsDateLocaleLoading(true);
        const locale =
          language === FALLBACK_LANGUAGE
            ? enUS
            : await dateLocaleMap[language]();

        setDateLocale(locale);
      } catch (e) {
        console.error('Error while loading date locale: ', e);
        setDateLocale(enUS);
      } finally {
        setIsDateLocaleLoading(false);
      }
    }

    load();
  }, [language]);

  return { dateLocale, isDateLocaleLoading };
}
